import React, { useState } from "react";
import {
  X, FileText, User, Calendar, CheckCircle,
  XCircle, Send, Loader, Stethoscope, ExternalLink,
} from "lucide-react";
import { useAuth, apiFetch } from "../../../../context/AuthContext";

const apiBaseURL = process.env.REACT_APP_BASE_URL;

/* ─── helpers (inlined to keep the file self-contained) ─── */
const getStatusColor = (status, theme) => {
  switch (status) {
    case "approved": return theme === "dark" ? "#10b981" : "#059669";
    case "pending": return theme === "dark" ? "#f59e0b" : "#d97706";
    case "rejected": return theme === "dark" ? "#ef4444" : "#dc2626";
    default: return "#2e7cba";
  }
};

const getStatusText = (status, t) =>
  ({ pending: t.pending, approved: t.approved, rejected: t.rejected }[status] || status);

const getLeaveTypeText = (type, t) =>
  ({ annual: t.annual, sick: t.sick, maternity: t.maternity, emergency: t.emergency, wfh: t.wfh }[type] || type);

const calculateDuration = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  return Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24)) + 1;
};

const getCurrentViewType = () =>
  String(localStorage.getItem("managerViewType") || localStorage.getItem("viewType") || "admin").toLowerCase();



/* ─────────────────────────────────────────────────────────── */

const LeaveDetailsModal = ({
  selectedLeave,
  theme, isRTL, language, t,
  decision, onDecisionChange,
  comments, onCommentsChange,
  isSubmitting,
  onClose, onSubmit,
  userRole, profile,
}) => {
  // Hooks must be called unconditionally
  const { hasFeatureAccess } = useAuth();
  const currentViewType = getCurrentViewType();
  const [isOpeningPrescription, setIsOpeningPrescription] = useState(false);
  const [prescriptionError, setPrescriptionError] = useState("");

  // The prescription endpoint is authenticated, so it can't be dropped into an
  // <img>/<a href>. Fetch it as a blob and hand the browser an object URL.
  const handleViewPrescription = async () => {
    if (!selectedLeave?.id) return;

    setIsOpeningPrescription(true);
    setPrescriptionError("");

    try {
      const res = await apiFetch(
        `${apiBaseURL}/api/leaves/${selectedLeave.id}/prescription`,
        { method: "GET" }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || data?.message || t.prescriptionLoadFailed);
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const opened = window.open(url, "_blank", "noopener,noreferrer");

      if (!opened) {
        // Popup blocked — fall back to a download so the file isn't lost.
        const a = document.createElement("a");
        a.href = url;
        a.download = `prescription_${selectedLeave.id}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }

      // Give the new tab time to take ownership before releasing the URL.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setPrescriptionError(err?.message || t.prescriptionLoadFailed);
    } finally {
      setIsOpeningPrescription(false);
    }
  };

  if (!selectedLeave) return null;

  const normalizeStatus = (v) => (typeof v === "string" ? v.trim().toLowerCase() : "");
  const normalizeLeaveType = (v) => (typeof v === "string" ? v.trim().toLowerCase() : "");
  const isManagerInManagerView = userRole === "manager" && currentViewType === "manager";
  const isPrivilegedRole = userRole === "admin" || userRole === "superadmin";
  const hasAdminLevel = !!(profile?.adminLevel || profile?.adminLevelId);

  const baseApprovePermission = isPrivilegedRole || isManagerInManagerView || hasAdminLevel || hasFeatureAccess("leaves", "approve");
  const baseRejectPermission = isPrivilegedRole || isManagerInManagerView || hasAdminLevel || hasFeatureAccess("leaves", "reject");

  // Visibility ≠ authority. A manager sees every request from their first-
  // and second-line reports, but the workflow alone decides who may act on
  // one. `canAct` is computed server-side and is true only when the workflow
  // instance is currently waiting on this user's step — so a manager who
  // isn't in the workflow, or who is a later-stage approver whose turn hasn't
  // come, gets a read-only view. Requests with no workflow attached are never
  // actionable from this page.
  const isManager = userRole === "manager";
  const isWorkflowTurn = selectedLeave?.canAct === true;
  const blockedByWorkflow = isManager && !isWorkflowTurn;

  const hasApprovePermission = !blockedByWorkflow && baseApprovePermission;
  const hasRejectPermission = !blockedByWorkflow && baseRejectPermission;
  const hasAnyPermission = hasApprovePermission || hasRejectPermission;

  // Only worth explaining when the user would otherwise have had buttons.
  const showWorkflowNotice =
    blockedByWorkflow && (baseApprovePermission || baseRejectPermission);

  const statusColor = getStatusColor(normalizeStatus(selectedLeave.status), theme);

  /* ── style objects ── */
  const overlay = {
    position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
    backgroundColor: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)",
    display: "flex", alignItems: "center", justifyContent: "center",
    zIndex: 1000, padding: "clamp(10px,2vw,20px)",
    direction: isRTL ? "rtl" : "ltr",
  };

  const modalContent = {
    background: theme === "light" ? "rgba(255,255,255,0.95)" : "rgba(30,41,59,0.95)",
    padding: "clamp(20px,4vw,28px)", borderRadius: "clamp(12px,2vw,20px)",
    width: "min(90vw,600px)", maxHeight: "85vh", overflowY: "auto",
    boxShadow: theme === "light" ? "0 10px 40px rgba(0,0,0,0.15)" : "0 10px 40px rgba(0,0,0,0.5)",
    color: theme === "light" ? "#0f172a" : "#ffffff",
    border: theme === "dark" ? "1px solid rgba(255,255,255,0.15)" : "1px solid rgba(0,0,0,0.05)",
    boxSizing: "border-box", direction: isRTL ? "rtl" : "ltr",
    scrollbarWidth: "none", msOverflowStyle: "none",
  };

  const divider = {
    borderBottom: theme === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
  };

  const infoBox = {
    background: theme === "light" ? "rgba(0,0,0,0.03)" : "rgba(255,255,255,0.05)",
    border: theme === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
    borderRadius: "12px", padding: "16px",
  };

  const detailLabel = {
    fontSize: "12px",
    color: theme === "dark" ? "rgba(255,255,255,0.6)" : "rgba(26,26,26,0.6)",
    fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.8px",
    marginBottom: "8px", textAlign: isRTL ? "right" : "left",
  };

  const detailValue = {
    fontSize: "15px", fontWeight: "600", lineHeight: "1.5",
    textAlign: isRTL ? "right" : "left",
  };

  const secondaryButton = {
    background: theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
    color: theme === "dark" ? "#ffffff" : "#374151",
    border: theme === "dark" ? "2px solid rgba(255,255,255,0.15)" : "2px solid rgba(0,0,0,0.1)",
    borderRadius: "clamp(10px,1.5vw,14px)",
    padding: "clamp(10px,2vw,14px) clamp(16px,3vw,24px)",
    fontSize: "clamp(11px,1.5vw,14px)", fontWeight: "700", cursor: "pointer",
    display: "flex", alignItems: "center", gap: "8px",
  };

  const primaryButton = {
    background: "linear-gradient(135deg,#2e7cba,#2e7cba)", color: "white",
    border: "none", borderRadius: "clamp(10px,1.5vw,14px)",
    padding: "clamp(10px,2vw,14px) clamp(16px,3vw,24px)",
    fontSize: "clamp(11px,1.5vw,14px)", fontWeight: "700", cursor: "pointer",
    display: "flex", alignItems: "center", gap: "8px",
    transition: "all 0.3s ease",
  };

  const decisionButtonBase = {
    padding: "16px 20px", borderRadius: "12px", cursor: "pointer",
    fontSize: "16px", fontWeight: "700",
    display: "flex", alignItems: "center", justifyContent: "center", gap: "10px",
    transition: "all 0.3s ease",
  };

  const textarea = {
    width: "100%", minHeight: "120px",
    background: theme === "light" ? "#f9fafb" : "rgba(255,255,255,0.05)",
    border: theme === "light" ? "1px solid rgba(0,0,0,0.2)" : "1px solid rgba(255,255,255,0.2)",
    borderRadius: "12px", padding: "12px", fontSize: "14px",
    color: theme === "light" ? "#0f172a" : "#ffffff",
    outline: "none", resize: "vertical", fontFamily: "inherit",
    textAlign: isRTL ? "right" : "left", direction: isRTL ? "rtl" : "ltr",
  };

  const isMobile = window.innerWidth < 768;

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modalContent} onClick={(e) => e.stopPropagation()}>

        {/* ── Header ── */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px", paddingBottom: "16px", ...divider }}>
          <div style={{ fontSize: "clamp(18px,3vw,24px)", fontWeight: "700", display: "flex", alignItems: "center", gap: "12px" }}>
            <FileText size={24} color="#2e7cba" />
            {t.leaveRequestReview}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: theme === "dark" ? "white" : "#1a1a1a", cursor: "pointer", padding: "8px", borderRadius: "8px" }} className="close-button">
            <X size={24} />
          </button>
        </div>

        {/* ── Employee Info ── */}
        <div style={{ marginBottom: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px", paddingBottom: "12px", ...divider }}>
            <User size={18} />
            <div style={{ fontSize: "18px", fontWeight: "700", flex: 1, textAlign: isRTL ? "right" : "left" }}>{t.employeeInformation}</div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "16px", padding: "clamp(16px,3vw,20px)", background: theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)", borderRadius: "12px", border: theme === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.05)" }}>
            <div style={{ width: "clamp(48px,8vw,56px)", height: "clamp(48px,8vw,56px)", background: "linear-gradient(135deg,#2e7cba,#2e7cba)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", color: "white", fontSize: "clamp(20px,3vw,24px)", fontWeight: "700", flexShrink: 0 }}>
              {selectedLeave.employeeName?.charAt(0).toUpperCase()}
            </div>
            <div style={{ textAlign: isRTL ? "right" : "left" }}>
              <div style={{ fontSize: "clamp(18px,2.5vw,20px)", fontWeight: "700", marginBottom: "4px" }}>{selectedLeave.employeeName}</div>
              <div style={{ fontSize: "14px", color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(26,26,26,0.7)" }}>
                {t.requestedOn}{" "}
                {new Date(selectedLeave.created_at || selectedLeave.requestDate).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US")}
              </div>
            </div>
          </div>
        </div>

        {/* ── Request Details ── */}
        <div style={{ marginBottom: "24px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", marginBottom: "16px", paddingBottom: "12px", flexWrap: "wrap", ...divider }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <Calendar size={18} />
              <div style={{ fontSize: "18px", fontWeight: "700" }}>{t.requestDetails}</div>
            </div>
            <span style={{ background: `${getStatusColor(normalizeLeaveType(selectedLeave.leaveType || selectedLeave.type), theme)}20`, color: getStatusColor(normalizeLeaveType(selectedLeave.leaveType || selectedLeave.type), theme), padding: "4px 12px", borderRadius: "8px", fontSize: "12px", fontWeight: "600", textTransform: "capitalize" }}>
              {getLeaveTypeText(normalizeLeaveType(selectedLeave.leaveType || selectedLeave.type), t)}
            </span>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2,1fr)", gap: "16px" }}>
            {/* Start Date */}
            <div style={infoBox}>
              <div style={detailLabel}>{t.startDate}</div>
              <div style={detailValue}>{new Date(selectedLeave.startDate).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US", { month: "long", day: "numeric", year: "numeric" })}</div>
            </div>
            {/* End Date */}
            <div style={infoBox}>
              <div style={detailLabel}>{t.endDate}</div>
              <div style={detailValue}>{new Date(selectedLeave.endDate).toLocaleDateString(language === "ar" ? "ar-EG" : "en-US", { month: "long", day: "numeric", year: "numeric" })}</div>
            </div>
            {/* Duration */}
            <div style={infoBox}>
              <div style={detailLabel}>{t.duration}</div>
              <div style={detailValue}>
                {selectedLeave.days || calculateDuration(selectedLeave.startDate, selectedLeave.endDate)} {t.days}
              </div>
            </div>
            {/* Status */}
            <div style={infoBox}>
              <div style={detailLabel}>{t.status}</div>
              <span style={{ padding: "6px 12px", background: `${statusColor}20`, color: statusColor, borderRadius: "8px", fontSize: "12px", fontWeight: "600", textTransform: "capitalize", display: "inline-block" }}>
                {getStatusText(normalizeStatus(selectedLeave.status), t)}
              </span>
            </div>
          </div>

          {/* Reason */}
          {selectedLeave.reason && (
            <div style={{ marginTop: "16px", padding: "clamp(16px,3vw,20px)", background: theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)", borderRadius: "12px", border: theme === "dark" ? "2px solid rgba(255,255,255,0.08)" : "2px solid rgba(0,0,0,0.05)" }}>
              <div style={{ ...detailLabel, display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                <FileText size={14} />{t.reasonForLeave}
              </div>
              <p style={{ fontSize: "15px", fontWeight: "600", lineHeight: "1.7", margin: 0, textAlign: isRTL ? "right" : "left" }}>
                {selectedLeave.reason}
              </p>
            </div>
          )}

          {/* ── Prescription (sick leave) ── */}
          {normalizeLeaveType(selectedLeave.leaveType || selectedLeave.type) === "sick" && (
            <div style={{ marginTop: "16px", padding: "clamp(16px,3vw,20px)", background: theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)", borderRadius: "12px", border: theme === "dark" ? "2px solid rgba(255,255,255,0.08)" : "2px solid rgba(0,0,0,0.05)" }}>
              <div style={{ ...detailLabel, display: "flex", alignItems: "center", gap: "6px", marginBottom: "12px" }}>
                <Stethoscope size={14} />{t.prescription}
              </div>

              {selectedLeave.prescriptionPath || selectedLeave.isPrescriptionUploaded ? (
                <button
                  onClick={handleViewPrescription}
                  disabled={isOpeningPrescription}
                  style={{
                    ...secondaryButton,
                    opacity: isOpeningPrescription ? 0.6 : 1,
                    cursor: isOpeningPrescription ? "wait" : "pointer",
                  }}
                  className="enhanced-button secondary"
                >
                  {isOpeningPrescription ? (
                    <><Loader size={16} className="spin" />{t.openingPrescription}</>
                  ) : (
                    <><ExternalLink size={16} />{t.viewPrescription}</>
                  )}
                </button>
              ) : (
                <div style={{ fontSize: "14px", fontWeight: 600, color: theme === "dark" ? "rgba(255,255,255,0.55)" : "rgba(26,26,26,0.55)", textAlign: isRTL ? "right" : "left" }}>
                  {t.prescriptionMissing}
                </div>
              )}

              {prescriptionError && (
                <div style={{ marginTop: "10px", fontSize: "13px", fontWeight: 600, color: getStatusColor("rejected", theme), textAlign: isRTL ? "right" : "left" }}>
                  {prescriptionError}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Read-only notice: visible to this manager, but not their call ── */}
        {selectedLeave.status === "pending" && showWorkflowNotice && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              marginBottom: "24px",
              padding: "clamp(14px,3vw,18px)",
              borderRadius: "12px",
              background: theme === "dark" ? "rgba(245,158,11,0.1)" : "rgba(217,119,6,0.08)",
              border: `1px solid ${getStatusColor("pending", theme)}55`,
              color: getStatusColor("pending", theme),
              fontSize: "14px",
              fontWeight: 600,
              lineHeight: 1.6,
              textAlign: isRTL ? "right" : "left",
            }}
          >
            <FileText size={18} style={{ flexShrink: 0 }} />
            <span>
              {isRTL
                ? "هذا الطلب معروض للاطلاع فقط — الموافقة عليه ليست من دورك في مسار الاعتماد."
                : "This request is view-only — it is not your turn in the approval workflow."}
            </span>
          </div>
        )}

        {/* ── Decision Section (pending only) ── */}
        {selectedLeave.status === "pending" && hasAnyPermission && (
          <div style={{ marginBottom: "24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px", paddingBottom: "12px", ...divider }}>
              <CheckCircle size={18} />
              <div style={{ fontSize: "18px", fontWeight: "700" }}>{t.yourDecision}</div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit,minmax(150px,1fr))", gap: "12px", marginBottom: "16px" }}>
              {hasApprovePermission && (
                <button
                  style={{
                    ...decisionButtonBase,
                    border: `2px solid ${decision === "approved" ? getStatusColor("approved", theme) : theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"}`,
                    background: decision === "approved" ? `linear-gradient(135deg,${getStatusColor("approved", theme)},${getStatusColor("approved", theme)}dd)` : "transparent",
                    color: decision === "approved" ? "white" : theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(26,26,26,0.7)",
                    boxShadow: decision === "approved" ? `0 8px 24px ${getStatusColor("approved", theme)}40` : "none",
                  }}
                  onClick={() => onDecisionChange("approved")}
                  disabled={isSubmitting}
                >
                  <CheckCircle size={20} />{t.approve}
                </button>
              )}
              {hasRejectPermission && (
                <button
                  style={{
                    ...decisionButtonBase,
                    border: `2px solid ${decision === "rejected" ? getStatusColor("rejected", theme) : theme === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.2)"}`,
                    background: decision === "rejected" ? `linear-gradient(135deg,${getStatusColor("rejected", theme)},${getStatusColor("rejected", theme)}dd)` : "transparent",
                    color: decision === "rejected" ? "white" : theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(26,26,26,0.7)",
                    boxShadow: decision === "rejected" ? `0 8px 24px ${getStatusColor("rejected", theme)}40` : "none",
                  }}
                  onClick={() => onDecisionChange("rejected")}
                  disabled={isSubmitting}
                >
                  <XCircle size={20} />{t.reject}
                </button>
              )}
            </div>

            {decision && (
              <div>
                <div style={{ ...detailLabel, display: "flex", alignItems: "center", gap: "6px", marginBottom: "8px" }}>
                  <FileText size={14} />
                  {decision === "approved" ? t.approvalComments : t.reasonForRejection} {t.optional}
                </div>
                <textarea
                  style={textarea}
                  placeholder={decision === "approved" ? t.approvalCommentsPlaceholder : t.rejectionReasonPlaceholder}
                  value={comments}
                  onChange={(e) => onCommentsChange(e.target.value)}
                  disabled={isSubmitting}
                  className="enhanced-input"
                />
              </div>
            )}
          </div>
        )}

        {/* ── Action Buttons ── */}
        <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: "24px", paddingTop: "16px", flexWrap: "wrap", borderTop: theme === "dark" ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)" }}>
          <button style={secondaryButton} onClick={onClose} disabled={isSubmitting} className="enhanced-button secondary">
            {t.cancel}
          </button>

          {selectedLeave.status === "pending" && (() => {
            const canSubmit =
              (decision === "approved" && hasApprovePermission) ||
              (decision === "rejected" && hasRejectPermission);

            return hasAnyPermission && canSubmit ? (
              <button
                style={{ ...primaryButton, opacity: !decision || isSubmitting ? 0.5 : 1 }}
                onClick={onSubmit}
                disabled={!decision || isSubmitting}
                className="enhanced-button primary"
              >
                {isSubmitting ? (
                  <><Loader size={18} className="spin" />{t.processing}</>
                ) : (
                  <>
                    <Send size={18} />
                    {decision === "approved" ? t.submitApproval : decision === "rejected" ? t.submitRejection : t.submitDecision}
                  </>
                )}
              </button>
            ) : null;
          })()}
        </div>
      </div>
    </div>
  );
};
export default LeaveDetailsModal;